/**
 * 客户端发版检测（/version.json + mp.api 发版日志）。
 *
 * 边界：
 * - version.json 只作版本信号；提示文案 / publishType 以 API 为准
 * - API skip → 推进 baseline 不提示；retry → 不推进，下轮重试
 * - Hash 路由硬跳必须带 #
 * - 升级 UI 统一走 resolveUpgradeUi，禁止高风险时静默硬刷
 * - 10s 倒计时中途出现 mutate 会升为弹窗（由外部 onRiskDuringCountdown）
 */
import { config } from "/@/cool/config";
import {
	hasPendingMutations,
	hasRiskyRecent,
	onActionHistoryChange,
	pushButton,
	pushRequestStart
} from "/@/cool/utils/action-history";

/** 本仓端标识（shop / repair 同步时各写各的） */
export const APP_NAME = "shop";

export type PublishType = "force" | "normal" | "hold";

export type VersionInfo = {
	version: string;
	remark: string;
	publishType: PublishType;
	message: string;
	changes: string[];
};

/** API 返回的有效发版配置（hold 已在 fetch 阶段转为 skip） */
export type ReleaseConfig = {
	version: string;
	publishType: Exclude<PublishType, "hold">;
	message: string;
	changes: string[];
};

export type VersionRouterLike = {
	beforeEach: (guard: (to: any, from: any, next: any) => void) => void;
};

export type StartVersionCheckOptions = {
	/** localhost:9001 等开发站可 silent */
	silent?: boolean;
	pollMs?: number;
	countdownMs?: number;
	router?: VersionRouterLike;
	/** 启用轻量按钮点击记录 */
	trackButtons?: boolean;
	/** 需要确认时（弹窗）；返回 true 表示用户同意刷新 */
	onConfirmReload: (info: VersionInfo, reason: "risk" | "force-pending") => Promise<boolean>;
};

const VERSION_URL = "/version.json";
const BANNER_ID = "sw-version-banner";
const DISMISS_KEY = "sw-web-version-dismissed";
const STYLE_ID = "sw-version-banner-style";
const RELEASE_FETCH_TIMEOUT_MS = 5_000;

let baseline: string | null = null;
let pendingUpdate = false;
let latestInfo: VersionInfo | null = null;
let started = false;
let reloading = false;
let prompting = false;
let countdownTimer: ReturnType<typeof setTimeout> | null = null;
let countdownRemain = 0;
let countdownTick: ReturnType<typeof setInterval> | null = null;
let optionsRef: StartVersionCheckOptions | null = null;
let loadBusyCount = 0;

function isSilentOrigin(): boolean {
	return location.origin === "http://localhost:9001";
}

type VersionCheckTestHook = {
	enabled?: boolean;
	countdownMs?: number;
	pollMs?: number;
};

/** 本地联调：`?__vc=1&__vcCountdown=2000&__vcPoll=800`，或 `window.__VERSION_CHECK_TEST__` */
function getQueryOverrides(): {
	enabled: boolean;
	countdownMs?: number;
	pollMs?: number;
} {
	try {
		const hook = (window as unknown as { __VERSION_CHECK_TEST__?: VersionCheckTestHook })
			.__VERSION_CHECK_TEST__;
		const fromSearch = new URLSearchParams(location.search);
		const hash = location.hash || "";
		const hashQuery = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
		const fromHash = new URLSearchParams(hashQuery);
		const get = (key: string) => fromSearch.get(key) || fromHash.get(key);
		const enabled = hook?.enabled === true || get("__vc") === "1";
		const countdownRaw = Number(hook?.countdownMs ?? get("__vcCountdown") ?? "");
		const pollRaw = Number(hook?.pollMs ?? get("__vcPoll") ?? "");
		return {
			enabled,
			countdownMs:
				Number.isFinite(countdownRaw) && countdownRaw > 0 ? countdownRaw : undefined,
			pollMs: Number.isFinite(pollRaw) && pollRaw > 0 ? pollRaw : undefined
		};
	} catch {
		return { enabled: false };
	}
}

function advanceBaseline(version: string): void {
	baseline = version;
}

function toVersionInfo(configLike: ReleaseConfig, remoteVersion: string): VersionInfo {
	return {
		version: remoteVersion,
		remark: "",
		publishType: configLike.publishType,
		message: configLike.message,
		changes: configLike.changes
	};
}

/** 仅消费 version.json 的 version 字段 */
async function fetchVersionInfo(): Promise<{ version: string } | null> {
	try {
		const res = await fetch(VERSION_URL, { cache: "no-store" });
		if (!res.ok) return null;
		const data = (await res.json()) as { version?: unknown };
		if (typeof data.version !== "string" || !data.version) return null;
		return { version: data.version };
	} catch {
		return null;
	}
}

function releaseLatestUrl(): string {
	// 各端打各自 api：mp→mp.api / shop→shop.api / repair→repair.api
	const base = String((config as { baseUrl?: string }).baseUrl || "").replace(/\/$/, "");
	return `${base}/releaseNote/latest?app=${encodeURIComponent(APP_NAME)}`;
}

function normalizeChanges(raw: unknown): string[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.filter((item): item is string => typeof item === "string")
		.map((item) => item.trim())
		.filter(Boolean);
}

/**
 * 检测到 version 变化时拉取一次发版配置。
 * - retry：网络/超时/非 200/非 JSON → 不推进 baseline
 * - skip：无记录 / version 不匹配 / hold / 文案为空 → 推进 baseline
 * - ReleaseConfig：有效记录
 */
export async function fetchReleaseConfig(
	remoteVersion: string
): Promise<"skip" | "retry" | ReleaseConfig> {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), RELEASE_FETCH_TIMEOUT_MS);

	try {
		const res = await fetch(releaseLatestUrl(), {
			cache: "no-store",
			signal: controller.signal
		});
		if (!res.ok) return "retry";

		let body: { code?: unknown; data?: unknown };
		try {
			body = (await res.json()) as { code?: unknown; data?: unknown };
		} catch {
			return "retry";
		}

		if (body.code !== 1000) return "retry";
		if (body.data == null) return "skip";
		if (typeof body.data !== "object") return "skip";

		const data = body.data as Record<string, unknown>;
		const boundVersion = typeof data.version === "string" ? data.version : "";
		// 空串 = 不绑定；非空须等于当前 version.json
		if (boundVersion && boundVersion !== remoteVersion) return "skip";

		const publishType = data.publishType;
		if (publishType === "hold") return "skip";
		if (publishType !== "force" && publishType !== "normal") return "skip";

		const message = typeof data.message === "string" ? data.message.trim() : "";
		const changes = normalizeChanges(data.changes);
		if (!message && changes.length === 0) return "skip";

		return {
			version: boundVersion,
			publishType,
			message: message || "已发布新版本，即将刷新以使用最新内容",
			changes
		};
	} catch {
		return "retry";
	} finally {
		clearTimeout(timer);
	}
}

export async function clearCaches(): Promise<void> {
	if ("caches" in window) {
		try {
			const keys = await caches.keys();
			await Promise.all(keys.map((key) => caches.delete(key)));
		} catch {
			/* ignore */
		}
	}

	if ("serviceWorker" in navigator) {
		try {
			const regs = await navigator.serviceWorker.getRegistrations();
			await Promise.all(regs.map((reg) => reg.unregister()));
		} catch {
			/* ignore */
		}
	}
}

/** 硬刷新：清 Cache Storage + 注销 SW，再 reload/assign。保持当前入口 HTML。 */
export async function hardReload(hashFullPath?: string): Promise<void> {
	if (reloading) return;
	reloading = true;
	try {
		// SW/Cache 清理卡住时不能阻断刷新
		await Promise.race([clearCaches(), new Promise<void>((r) => setTimeout(r, 2000))]);
	} catch {
		/* ignore */
	}

	if (hashFullPath) {
		const path = hashFullPath.startsWith("/") ? hashFullPath : `/${hashFullPath}`;
		location.assign(`${location.pathname}${location.search}#${path}`);
		return;
	}

	location.reload();
}

function ensureBannerStyle(): void {
	if (document.getElementById(STYLE_ID)) return;
	const style = document.createElement("style");
	style.id = STYLE_ID;
	style.textContent = `
.sw-version-banner {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100000;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 10px 16px;
	background: #303133;
	color: #fff;
	font-size: 14px;
	line-height: 1.4;
	box-shadow: 0 2px 8px rgba(0,0,0,.15);
}
.sw-version-banner__btn {
	border: 1px solid rgba(255,255,255,.55);
	background: transparent;
	color: #fff;
	border-radius: 4px;
	padding: 2px 10px;
	cursor: pointer;
}
.sw-version-banner__btn:hover { background: rgba(255,255,255,.12); }
`;
	document.head.appendChild(style);
}

function clearCountdown(): void {
	if (countdownTimer) {
		clearTimeout(countdownTimer);
		countdownTimer = null;
	}
	if (countdownTick) {
		clearInterval(countdownTick);
		countdownTick = null;
	}
	countdownRemain = 0;
}

function removeBanner(): void {
	document.getElementById(BANNER_ID)?.remove();
	clearCountdown();
}

function isDismissedFor(version: string): boolean {
	try {
		return sessionStorage.getItem(DISMISS_KEY) === version;
	} catch {
		return false;
	}
}

function markDismissed(version: string): void {
	try {
		sessionStorage.setItem(DISMISS_KEY, version);
	} catch {
		/* ignore */
	}
}

function showCountdownBanner(info: VersionInfo, force: boolean): void {
	ensureBannerStyle();
	removeBanner();

	const ms = optionsRef?.countdownMs ?? 10_000;
	countdownRemain = Math.ceil(ms / 1000);

	const bar = document.createElement("div");
	bar.id = BANNER_ID;
	bar.className = "sw-version-banner";
	bar.setAttribute("role", "status");

	const text = document.createElement("span");
	text.className = "sw-version-banner__text";

	const refreshBtn = document.createElement("button");
	refreshBtn.type = "button";
	refreshBtn.className = "sw-version-banner__btn";
	refreshBtn.textContent = "立即刷新";
	refreshBtn.addEventListener("click", () => {
		void hardReload();
	});

	const renderText = () => {
		text.textContent = `${info.message}（${countdownRemain}s）`;
	};
	renderText();

	bar.append(text, refreshBtn);
	document.body.prepend(bar);

	const finishCountdown = () => {
		// 只清计时器，保留横幅文案到页面开始卸载；避免 clearCountdown 误杀即将触发的 reload
		if (countdownTimer) {
			clearTimeout(countdownTimer);
			countdownTimer = null;
		}
		if (countdownTick) {
			clearInterval(countdownTick);
			countdownTick = null;
		}
		countdownRemain = 0;
		if (loadBusyCount > 0) {
			removeBanner();
			pendingUpdate = true;
			return;
		}
		void hardReload();
	};

	countdownTick = setInterval(() => {
		countdownRemain -= 1;
		if (countdownRemain <= 0) {
			renderText();
			finishCountdown();
			return;
		}
		renderText();
	}, 1000);

	countdownTimer = setTimeout(() => {
		finishCountdown();
	}, ms);

	// force 顶栏不可关闭；normal 也不提供关闭（按计划 10s 后刷新）。暂不仅通过弹窗。
	void force;
}

async function promptConfirm(info: VersionInfo, reason: "risk" | "force-pending"): Promise<void> {
	if (!optionsRef) return;
	if (prompting) return;
	if (reason === "risk" && info.publishType === "normal" && isDismissedFor(info.version)) {
		return;
	}

	prompting = true;
	removeBanner();

	try {
		const ok = await optionsRef.onConfirmReload(info, reason);
		if (ok) {
			await hardReload();
			return;
		}
		if (info.publishType === "normal") {
			markDismissed(info.version);
		}
	} finally {
		prompting = false;
	}
}

function needsModal(info: VersionInfo): "risk" | "force-pending" | null {
	if (info.publishType === "force") {
		if (hasPendingMutations()) return "force-pending";
		return null;
	}
	if (hasRiskyRecent() || hasPendingMutations()) return "risk";
	return null;
}

/** 统一升级 UI 入口 */
export async function resolveUpgradeUi(info: VersionInfo): Promise<void> {
	if (prompting || reloading) return;

	pendingUpdate = true;
	latestInfo = info;

	if (loadBusyCount > 0) {
		return;
	}

	const modalReason = needsModal(info);
	if (modalReason) {
		await promptConfirm(info, modalReason);
		return;
	}

	// 已在倒计时中：勿重建横幅，否则 clearCountdown 会打断即将触发的硬刷
	if (countdownTimer || countdownTick) return;

	if (info.publishType === "force") {
		showCountdownBanner(info, true);
		return;
	}

	// normal 且无高风险
	if (isDismissedFor(info.version)) return;
	showCountdownBanner(info, false);
}

function handleHistoryDuringCountdown(): void {
	if (!latestInfo || !countdownTimer) return;
	if (!hasRiskyRecent() && !hasPendingMutations()) return;

	clearCountdown();
	removeBanner();
	void promptConfirm(latestInfo, latestInfo.publishType === "force" ? "force-pending" : "risk");
}

async function checkForUpdate(): Promise<void> {
	if (!optionsRef || optionsRef.silent) return;

	const remote = await fetchVersionInfo();
	if (!remote) return;

	if (baseline === null) {
		advanceBaseline(remote.version);
		return;
	}

	if (remote.version === baseline) return;

	const release = await fetchReleaseConfig(remote.version);
	if (release === "retry") {
		// 保守等待：不推进 baseline，下轮重试
		return;
	}
	if (release === "skip") {
		advanceBaseline(remote.version);
		return;
	}

	advanceBaseline(remote.version);
	await resolveUpgradeUi(toVersionInfo(release, remote.version));
}

function bindButtonTracking(): void {
	document.addEventListener(
		"click",
		(ev) => {
			const el = (ev.target as HTMLElement | null)?.closest?.(
				"button, [role='button'], .el-button"
			) as HTMLElement | null;
			if (!el) return;
			const label =
				el.getAttribute("aria-label") ||
				el.getAttribute("data-track") ||
				el.innerText ||
				el.textContent ||
				"";
			pushButton(label);
		},
		true
	);
}

function bindRouter(router: VersionRouterLike): void {
	router.beforeEach((to, _from, next) => {
		if (!pendingUpdate || reloading || prompting || loadBusyCount > 0) {
			next();
			return;
		}

		const info = latestInfo;
		if (!info || info.publishType === "hold") {
			next();
			return;
		}

		const modalReason = needsModal(info);
		if (modalReason) {
			// 高风险：禁止静默硬刷，弹窗确认
			void (async () => {
				await promptConfirm(info, modalReason);
				if (reloading) {
					next(false);
					return;
				}
				next();
			})();
			return;
		}

		// 无高风险：取消 SPA 导航，硬跳到目标 hash 路由
		next(false);
		void hardReload(to.fullPath);
	});
}

export function startVersionCheck(options: StartVersionCheckOptions): void {
	if (started) return;

	const overrides = getQueryOverrides();
	const silent = overrides.enabled ? false : (options.silent ?? isSilentOrigin());
	const countdownMs = options.countdownMs ?? overrides.countdownMs ?? 10_000;
	const pollMs = options.pollMs ?? overrides.pollMs ?? 5 * 60 * 1000;
	optionsRef = { ...options, silent, countdownMs, pollMs };
	started = true;

	if (silent) return;

	// 便于本地/E2E 主动触发检测与注入历史
	(window as unknown as { __vcTest?: unknown }).__vcTest = {
		pushRequestStart,
		hasRiskyRecent,
		hasPendingMutations,
		checkForUpdate,
		getState: () => ({
			baseline,
			pendingUpdate,
			countdownMs,
			pollMs,
			loadBusyCount
		})
	};

	void checkForUpdate();

	window.setInterval(() => {
		void checkForUpdate();
	}, pollMs);

	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState !== "visible") return;
		void (async () => {
			await checkForUpdate();
			if (pendingUpdate && latestInfo && latestInfo.publishType !== "hold") {
				await resolveUpgradeUi(latestInfo);
			}
		})();
	});

	onActionHistoryChange(() => {
		handleHistoryDuringCountdown();
	});

	if (options.trackButtons !== false) {
		bindButtonTracking();
	}

	if (options.router) {
		bindRouter(options.router);
	}
}

export function isVersionPending(): boolean {
	return pendingUpdate;
}

export function isVersionCheckLoadBusy(): boolean {
	return loadBusyCount > 0;
}

/**
 * 3D 模型加载期间暂停自动 hardReload。
 * 仍会拉取 /version.json 并记下 pending，加载结束后再走现有分流。
 */
export function setVersionCheckLoadBusy(busy: boolean): void {
	if (busy) {
		loadBusyCount += 1;
		return;
	}

	loadBusyCount = Math.max(0, loadBusyCount - 1);
	if (loadBusyCount > 0 || !pendingUpdate || !latestInfo || prompting || reloading) {
		return;
	}
	if (!optionsRef || optionsRef.silent) {
		return;
	}
	void resolveUpgradeUi(latestInfo);
}

/** 仅供单测重置模块状态 */
export function resetVersionCheckForTest(): void {
	baseline = null;
	pendingUpdate = false;
	latestInfo = null;
	started = false;
	reloading = false;
	prompting = false;
	loadBusyCount = 0;
	clearCountdown();
	removeBanner();
	optionsRef = null;
	document.getElementById(STYLE_ID)?.remove();
}
